import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function Signup() {
  const { signup, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "", role: "taxpayer" });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const u = await signup(form);
      toast.success(`Welcome, ${u.full_name.split(" ")[0]}! Let's file your taxes.`);
      navigate(u.role === "ca" ? "/ca-portal" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="mt-2 text-sm text-muted-foreground">Free for ITR-1. Upgrade only when you need more.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="full_name">Full name (as per PAN)</Label>
              <Input id="full_name" data-testid="signup-name" required className="h-11 mt-1.5" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="signup-email" required className="h-11 mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="phone">Mobile (optional)</Label>
              <Input id="phone" data-testid="signup-phone" className="h-11 mt-1.5" placeholder="+91 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="signup-password" minLength={8} required className="h-11 mt-1.5" placeholder="Minimum 8 characters" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div>
              <Label>I'm a</Label>
              <RadioGroup
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
                className="mt-2 grid grid-cols-2 gap-2"
              >
                <Label htmlFor="r-taxpayer" className={`flex items-center gap-2 rounded-md border p-3 cursor-pointer ${form.role === "taxpayer" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <RadioGroupItem id="r-taxpayer" value="taxpayer" data-testid="signup-role-taxpayer" />
                  <span className="text-sm font-medium">Taxpayer</span>
                </Label>
                <Label htmlFor="r-ca" className={`flex items-center gap-2 rounded-md border p-3 cursor-pointer ${form.role === "ca" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <RadioGroupItem id="r-ca" value="ca" data-testid="signup-role-ca" />
                  <span className="text-sm font-medium">Chartered Accountant</span>
                </Label>
              </RadioGroup>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="signup-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Already registered? <Link to="/login" className="font-semibold text-foreground hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex relative bg-primary text-primary-foreground p-12 flex-col justify-between overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-primary-foreground text-primary font-display font-bold">R</div>
          <div className="font-display text-lg font-bold tracking-tight">CA Ritika Singh</div>
        </Link>
        <div className="relative max-w-md">
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight leading-tight">A Chartered Accountant and an AI, on the same screen.</h2>
          <ul className="mt-6 space-y-3 text-primary-foreground/85">
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> Free ITR-1 self-filing forever</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> 47 AI audit checks before submission</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> Old vs new regime comparison built-in</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> Talk to a real CA when needed</li>
          </ul>
        </div>
        <div className="relative text-xs text-primary-foreground/60 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> DPDP-ready · AES-256 encrypted</div>
      </div>
    </div>
  );
}
